// store/schedule/scheduleSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

/* ---------- THUNKS ---------- */

export const fetchSchedule = createAsyncThunk(
  'schedule/fetchSchedule',
  async (clubId) => {
    const { data } = await api.get(`/club/${clubId}/schedule`);
    return data;
  }
);

export const deleteOverride = createAsyncThunk(
  'schedule/deleteOverride',
  async ({ clubId, overrideId }) => {
    await api.delete(`/club/${clubId}/schedule/override/${overrideId}`);
    return overrideId;
  }
);

export const fetchConflicts = createAsyncThunk(
  'schedule/fetchConflicts',
  async (clubId) => {
    const { data } = await api.get(`/bookings/club/${clubId}/stats`, {
      params: { isConflict: true },
    });
    return data.bookings || [];
  }
);

/* ---------- HELPERS ---------- */

// Convert weekSchedule.days → flat schedule format for ScheduleWork compatibility
function daysToSchedule(days) {
  if (!days) return null;
  const schedule = {};
  for (const [dayKey, dayData] of Object.entries(days)) {
    schedule[dayKey] = dayData.schedule || [];
  }
  return schedule;
}

/* ---------- SLICE ---------- */

const scheduleSlice = createSlice({
  name: 'schedule',
  initialState: {
    currentSchedule: null,
    weekSchedule: null, // currentWeek from API
    upcomingChange: null, // upcomingChange from API
    newSchedule: null,
    conflicts: [],
    isEditing: false,
    loading: false,
  },
  reducers: {
    startEditing(state) {
      state.isEditing = true;
    },
    cancelEditing(state) {
      state.isEditing = false;
      state.newSchedule = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSchedule.fulfilled, (state, action) => {
        const { currentWeek, upcomingChange } = action.payload;
        state.weekSchedule = currentWeek;
        state.upcomingChange = upcomingChange;
        state.currentSchedule = daysToSchedule(currentWeek?.days);
        state.loading = false;
      })
      .addCase(fetchConflicts.fulfilled, (state, action) => {
        state.conflicts = action.payload;
        state.isEditing = action.payload.length > 0;
      })
      .addCase(deleteOverride.fulfilled, (state) => {
        state.upcomingChange = null;
        state.isEditing = false;
      })
      .addCase(fetchSchedule.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { startEditing, cancelEditing } = scheduleSlice.actions;

export default scheduleSlice.reducer;
